import { Link } from "react-router-dom";
import SectionHeading from "@/components/shared/SectionHeading";
import { LoadingGrid, ErrorState, EmptyState } from "@/components/shared/DataState";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getProvenResults } from "@/data/repository";
import { stripHtml } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";

function ResultCard({ r, idx }) {
  const cover = r.image || (Array.isArray(r.images) ? r.images[0] : "");
  const preview = stripHtml(r.story || "");
  return (
    <Link
      to={`/success-stories/proven-results/${r.id}`}
      data-testid={`proven-result-${idx}`}
      className="reveal group relative flex flex-col overflow-hidden border border-bronze/20 bg-cream"
      style={{ transitionDelay: `${idx * 80}ms` }}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        {cover ? (
          <img src={cover} alt={r.headline} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-beige text-bronze/40 text-[0.7rem] tracking-[0.22em] uppercase">No image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between text-cream">
          <span className="font-serif text-3xl md:text-4xl leading-tight">{r.metric}</span>
        </div>
      </div>
      <div className="p-7 flex flex-col flex-1">
        {(r.propertyType || r.sub) && <div className="eyebrow mb-2">{r.propertyType || r.sub}</div>}
        <h3 className="font-serif text-2xl md:text-3xl text-ink">{r.headline}</h3>
        {preview && <p className="mt-4 text-sm font-light leading-relaxed text-ink_soft/80 line-clamp-3">{preview}</p>}
        <span className="mt-auto pt-6 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze group-hover:text-bronze_dark">
          Read full case study <ArrowUpRight size={14} />
        </span>
      </div>
    </Link>
  );
}

export default function ProvenResultsSection({ withHeading = true }) {
  const { data: results, loading, error, reload } = useAsyncData(getProvenResults, []);
  return (
    <section data-testid="proven-results" className="py-16 md:py-24 bg-cream">
      <div className="container-page">
        {withHeading && (
          <SectionHeading
            eyebrow="Proven Results"
            title={<>Outcomes that <span className="italic text-bronze">speak.</span></>}
            sub="Each story below is a snapshot of strategy meeting patience and disciplined marketing."
          />
        )}

        {loading && <div className={withHeading ? "mt-14" : ""}><LoadingGrid count={2} /></div>}

        {!loading && error && <ErrorState onRetry={reload} />}

        {!loading && !error && (
          <div className={`${withHeading ? "mt-14" : ""} grid grid-cols-1 md:grid-cols-2 gap-7`}>
            {results.map((r, i) => (
              <ResultCard key={r.id || r.headline} r={r} idx={i} />
            ))}
          </div>
        )}

        {!loading && !error && results.length === 0 && (
          <EmptyState title="Case studies coming soon." />
        )}
      </div>
    </section>
  );
}
